import { ReportStatus, ReportTypeIdentifier } from '../../types/reports';
import type { RegisteredFieldLabels } from '../form/fieldTabRegistry';
import {
  ACCIDENT_TAB_ORDER,
  ACCIDENT_TAB_TITLES,
  ENVIRONMENTAL_TAB_ORDER,
  ENVIRONMENTAL_TAB_TITLES,
} from './reportTabTitles';

/**
 * Reine Logik zum Hinweis ueber der Reiterleiste (Phase 43, D-09/D-13, Sketch 026 Runde 2):
 * welche Variante gezeigt wird und wie die technischen Feldnamen aus
 * `changedFieldsSinceRevision` zu lesbaren Gruppen werden. Keine React-Importe -- muss unter
 * `environment: 'node'` ladbar bleiben.
 */

export type RevisionNoticeVariant = 'returned' | 'resubmitted';

export interface RevisionNoticeState {
  variant: RevisionNoticeVariant;
  showChanges: boolean;
}

/**
 * Welcher Hinweis gilt fuer Status, Begruendung und Rolle. `null` = kein Hinweis.
 * Nach der Genehmigung (und in Entwurfsstaenden) wird nichts gezeigt (O-4).
 */
export function revisionNoticeState(
  status: ReportStatus,
  reason: string | null | undefined,
  changedFields: string[] | null | undefined,
  isReviewer: boolean
): RevisionNoticeState | null {
  if (!reason || reason.trim() === '') return null;

  if (status === ReportStatus.REVISION_NEEDED) {
    return { variant: 'returned', showChanges: false };
  }

  if (status === ReportStatus.SUBMITTED) {
    return {
      variant: 'resubmitted',
      showChanges: isReviewer && changedFields !== null && changedFields !== undefined,
    };
  }

  return null;
}

/** Felder, die keine Feldkomponente anmeldet, aber in der Aenderungsliste auftauchen koennen. */
export const STATIC_FIELD_LABELS: Record<string, string> = { notes: 'Notizen' };

export interface ChangedFieldGroup {
  title: string | null;
  labels: string[];
}

export interface ChangedFieldGroups {
  groups: ChangedFieldGroup[];
  unlabeledCount: number;
}

/** Reiterreihenfolge und -titel je Meldungsart; `null` fuer die Formulare ohne Reiter (O-5). */
export function tabConfigFor(
  reportType: ReportTypeIdentifier
): { order: readonly string[]; titles: Record<string, string> } | null {
  if (reportType === ReportTypeIdentifier.ENVIRONMENTAL) {
    return { order: ENVIRONMENTAL_TAB_ORDER, titles: ENVIRONMENTAL_TAB_TITLES };
  }
  if (reportType === ReportTypeIdentifier.ACCIDENT) {
    return { order: ACCIDENT_TAB_ORDER, titles: ACCIDENT_TAB_TITLES };
  }
  return null;
}

/**
 * Ordnet die geaenderten Wurzelfelder ihren Reitern zu und uebersetzt sie in Beschriftungen.
 * Felder ohne Beschriftung erscheinen nie mit technischem Namen, sie werden nur gezaehlt.
 */
export function groupChangedFields(
  changed: readonly string[],
  labels: RegisteredFieldLabels,
  tabOrder: readonly string[] | null,
  tabTitles: Record<string, string> | null
): ChangedFieldGroups {
  const byTab = new Map<string | null, string[]>();
  let unlabeledCount = 0;

  for (const field of changed) {
    const entry = labels[field];
    const label = entry?.label ?? STATIC_FIELD_LABELS[field];
    if (!label) {
      unlabeledCount += 1;
      continue;
    }

    const tab = tabOrder !== null && entry?.tab && tabOrder.includes(entry.tab) ? entry.tab : null;
    const list = byTab.get(tab) ?? [];
    if (!list.includes(label)) list.push(label);
    byTab.set(tab, list);
  }

  const groups: ChangedFieldGroup[] = [];
  if (tabOrder !== null) {
    for (const tab of tabOrder) {
      const list = byTab.get(tab);
      if (list && list.length > 0) {
        groups.push({ title: tabTitles?.[tab] ?? tab, labels: list });
      }
    }
  }
  const rest = byTab.get(null);
  if (rest && rest.length > 0) groups.push({ title: null, labels: rest });

  return { groups, unlabeledCount };
}
